/**
 * The head of the theatre's rail: what else the movement is called, and the trainer's own tags on
 * it (canon `F1-szczegoly`, 10 October 2026; exercise-library R20 and R21).
 *
 * ── „Znane też jako" — the content language's aliases, and only when there are any ─────────
 * R20: the detail lists the aliases in the language being read; another language's aliases still
 * find the movement but are not listed, and a language with none shows nothing — no heading over
 * an empty line.
 *
 * ── „Twoje tagi" — on every movement the trainer can see, library and own alike ────────────
 * The chips with their ×, the „N z 10" the cap is read against, the field, and the sentence that
 * says nobody else sees them. The field is Base UI's Autocomplete: its list is the trainer's own
 * tags holding the typed words, with their counts, and the typed text itself as a new tag last.
 * Enter takes the highlighted line; with the list closed it adds what is typed.
 *
 * Base UI supplies the combobox semantics, the highlight and the dismissal; the popup wears the
 * app's one menu surface, which is the board's own `.menu` and `.mi`.
 */

import { Autocomplete } from '@base-ui/react/autocomplete';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import menu from '~/shared/ui/MenuSurface.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useExerciseTagEditor, type TagEditorNotice } from '../lib/useExerciseTagEditor';
import {
  MAX_TAGS_PER_EXERCISE,
  tagFocusedAfterRemoving,
  type TagSuggestion,
} from '../model/exerciseTags';

import { TagGlyph } from './ExerciseLibraryIcons';
import rail from './TheatreRail.module.css';
import tagParts from './tagParts.module.css';
import styles from './TheatreTags.module.css';

interface TheatreTagsProps {
  readonly exerciseId: number;
  /** The movement's aliases in the content language. Empty draws no line. */
  readonly aliases: readonly string[];
  readonly tags: readonly string[];
}

const NOTICE_KEYS: Readonly<Record<TagEditorNotice, string>> = {
  invalid: 'web:exerciseLibrary.tags.refusal.invalid',
  perExercise: 'web:exerciseLibrary.tags.refusal.perExercise',
  perTrainer: 'web:exerciseLibrary.tags.refusal.perTrainer',
  failed: 'web:exerciseLibrary.tags.refusal.failed',
};

export function TheatreTags({ exerciseId, aliases, tags }: TheatreTagsProps) {
  const { t } = useTranslation();
  const editor = useExerciseTagEditor(exerciseId, tags);
  const [open, setOpen] = useState(false);
  const fieldId = useId();
  const noticeId = useId();
  // The list hangs off the whole field, its hint included, rather than off the bare input.
  const field = useRef<HTMLLabelElement>(null);
  const chips = useRef<HTMLUListElement>(null);
  /* Where focus goes once a × has removed its own chip: the next chip's ×, else the previous
   * one's, else the field (`''`). Consumed by the render the saved set arrives in — the × that had
   * focus is gone by then, and focus would otherwise fall to the card. A failed write leaves the
   * set, and the focused ×, where they were. */
  const focusAfterRemoval = useRef<string | null>(null);
  useEffect(() => {
    const target = focusAfterRemoval.current;
    if (target === null) return;
    focusAfterRemoval.current = null;
    const remove =
      target === ''
        ? null
        : chips.current?.querySelector<HTMLElement>(`[data-tag-remove="${CSS.escape(target)}"]`);
    (remove ?? field.current?.querySelector('input'))?.focus();
  }, [tags]);
  // A refused or failed write keeps its chip and its focused ×, so there is nothing to move to.
  useEffect(() => {
    if (editor.notice !== null) focusAfterRemoval.current = null;
  }, [editor.notice]);

  return (
    <>
      {aliases.length > 0 && (
        <>
          <p className={styles.aliases}>
            {t('web:exerciseLibrary.theatre.aliases')}{' '}
            {aliases.map((alias, index) => (
              <span key={alias}>
                {index > 0 && ', '}
                <b className={styles.alias}>{alias}</b>
              </span>
            ))}
          </p>
          <hr className={rail.rule} />
        </>
      )}

      <section className={rail.block} aria-labelledby={`${fieldId}-title`}>
        <h3 className={`${String(rail.title)} ${String(styles.title)}`} id={`${fieldId}-title`}>
          <TagGlyph className={styles.glyph} />
          {t('web:exerciseLibrary.tags.title')}
          <span className={styles.count}>
            {t('web:exerciseLibrary.tags.countOf', { used: tags.length, max: MAX_TAGS_PER_EXERCISE })}
          </span>
        </h3>

        {tags.length > 0 && (
          <ul ref={chips} className={styles.chips} aria-label={t('web:exerciseLibrary.tags.title')}>
            {tags.map((tag) => (
              <li key={tag} className={tagParts.tag}>
                <span className={tagParts.text}>{tag}</span>
                <button
                  type="button"
                  className={tagParts.remove}
                  aria-label={t('web:exerciseLibrary.tags.remove', { tag })}
                  data-tag-remove={tag}
                  aria-disabled={editor.busy}
                  onClick={() => {
                    if (editor.busy) return;
                    focusAfterRemoval.current = tagFocusedAfterRemoving(tags, tag) ?? '';
                    editor.remove(tag);
                  }}
                >
                  <CloseGlyph className={tagParts.removeGlyph} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <Autocomplete.Root
          items={editor.suggestions}
          // The model has already filtered and ordered the list; Base UI must not filter it again
          // with its own substring rule, which does not fold Polish.
          filter={null}
          itemToStringValue={(item: TagSuggestion) => item.label}
          value={editor.text}
          onValueChange={(next, details) => {
            // A pressed line hands its own label to the field; the press itself already added it.
            if (details.reason === 'item-press') return;
            editor.setText(next);
          }}
          open={open && editor.suggestions.length > 0}
          onOpenChange={setOpen}
          autoHighlight
        >
          <label className={tagParts.field} ref={field}>
            <span className={hidden.srOnly}>{t('web:exerciseLibrary.tags.add')}</span>
            <Autocomplete.Input
              className={tagParts.fieldInput}
              aria-label={t('web:exerciseLibrary.tags.add')}
              placeholder={t('web:exerciseLibrary.tags.add')}
              maxLength={64}
              aria-describedby={editor.notice === null ? undefined : noticeId}
              aria-disabled={editor.busy}
              onKeyDown={(event) => {
                // With the list open, Enter belongs to the highlighted line. Closed — Escape
                // pressed, or nothing to suggest — it adds what is typed.
                if (event.key !== 'Enter' || (open && editor.suggestions.length > 0)) return;
                event.preventDefault();
                editor.add(editor.text);
              }}
            />
            <span className={tagParts.fieldHint} aria-hidden>
              {t('web:exerciseLibrary.tags.enterAdds')}
            </span>
          </label>

          <Autocomplete.Portal>
            <Autocomplete.Positioner
              className={menu.positioner}
              anchor={field}
              sideOffset={4}
              align="start"
            >
              <Autocomplete.Popup className={`${String(menu.popup)} ${String(styles.popup)}`}>
                <Autocomplete.List>
                  {(item: TagSuggestion) => (
                    <Autocomplete.Item
                      key={`${item.kind}:${item.key}`}
                      value={item}
                      className={`${String(menu.item)} ${
                        item.kind === 'create' ? String(styles.create) : ''
                      }`}
                      onClick={() => {
                        editor.add(item.label);
                      }}
                    >
                      <span className={menu.itemLabel}>
                        {item.kind === 'create'
                          ? t('web:exerciseLibrary.tags.create', { tag: item.label })
                          : item.label}
                      </span>
                      {item.kind === 'existing' && (
                        <span className={styles.uses}>
                          {t('web:exerciseLibrary.tags.uses', { count: item.count })}
                        </span>
                      )}
                    </Autocomplete.Item>
                  )}
                </Autocomplete.List>
              </Autocomplete.Popup>
            </Autocomplete.Positioner>
          </Autocomplete.Portal>
        </Autocomplete.Root>

        {/* Always mounted, so a refusal that appears is announced rather than inserted with its
          * region. The privacy sentence stands under it whatever happens. */}
        <p className={styles.notice} id={noticeId} role="status">
          {editor.notice === null ? '' : t(NOTICE_KEYS[editor.notice])}
        </p>
        <p className={styles.note}>{t('web:exerciseLibrary.tags.privacy')}</p>
      </section>

      <hr className={rail.rule} />
    </>
  );
}
